import "../GymListings/GymListings.scss";
import { useTranslation } from "react-i18next";
import React, { useRef, useState } from "react";
import { Form, FormInstance, Table, TableProps } from "antd";
import { useGetGymNotificationGymsQuery } from "../../api/queries";
import { ColumnsType } from "antd/es/table";
import MainTitle from "../../components/ui/MainTitle/MainTitle";
import FormItemInput from "../../components/ui/FormItemInput/FormItemInput";
import CustomButton from "../../components/ui/CustomButton/CustomButton";
import searchIconWhite from "../../assets/images/icons/search-icon-white.svg";
import { IGymNotificationGym } from "../../models/GymNotification";
import FormItemSelect from "../../components/ui/FormItemSelect/FormItemSelect";
import dots from "../../assets/images/icons/dots.svg";
import viewIcon from "../../assets/images/icons/eye-dark.svg";
import basketIcon from "../../assets/images/icons/basket-black.svg";
import { useUserGymNotificationMutations } from "../../api/mutations";
import { handleError } from "../../helper/errorHandler";
import { openSuccessMessage } from "../../store/message/messageSlice";
import useAppDispatch from "../../hooks/useAppDispatch";
import GymNotificationsUserPopup from "./GymNotificationsUserPopup/GymNotificationsUserPopup";
import dayjs from "dayjs";

const GymNotifications = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectItems = [
    { id: "COMING_SOON", label: t("ComingSoon") },
    { id: "OPENED", label: t("Opened") },
  ];

  const formRef = useRef<FormInstance>(null);
  const [tableParams, setTableParams] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });
  const [filters, setFilters] = useState<{
    searchValue: string | null;
    status: "COMING_SOON" | "OPENED" | null;
  }>({ searchValue: null, status: null });
  const { data = [], isLoading, refetch } = useGetGymNotificationGymsQuery(filters);
  const { deleteByGymListingId } = useUserGymNotificationMutations();
  const [selectedGymIdForView, setSelectedGymIdForView] = useState<string | null>(null);

  const handleTableChange: TableProps<IGymNotificationGym>["onChange"] = (pagination) => {
    setTableParams({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  const onFinish = (values: {
    searchValue: string | undefined;
    status: "COMING_SOON" | "OPENED" | undefined;
  }) => {
    const model = {
      searchValue: values.searchValue ? values.searchValue : null,
      status: values.status ? values.status : null,
    };
    setFilters(model);
  };

  const remove = async (gymListingId: string) => {
    deleteByGymListingId(gymListingId, {
      onSuccess: () => onSuccessFn("GymNotificationRemovedSuccessfully"),
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  const onSuccessFn = async (message: string) => {
    refetch();
    if (formRef.current) {
      formRef.current.setFieldsValue({
        searchValue: null,
        status: null,
      });
    }
    dispatch(openSuccessMessage(t(message)));
  };

  const columns: ColumnsType<IGymNotificationGym> = [
    {
      title: t("GymName"),
      render: (data: IGymNotificationGym) => {
        return data.gymName;
      },
    },
    {
      title: t("Location"),
      render: (data: IGymNotificationGym) => {
        return data.gymLocation;
      },
    },
    {
      title: t("Status"),
      render: (data: IGymNotificationGym) => {
        if (data.comingSoon) {
          return <span className={"status coming-soon"}>{t("ComingSoon")}</span>;
        } else {
          return <span className={"status approved"}>{t("Opened")}</span>;
        }
      },
    },
    {
      title: t("NotificationSent"),
      render: (data: IGymNotificationGym) => {
        if (data.notificationSent) {
          return (
            <span style={{ color: "#32BC69" }}>
              {t("Yes")}({dayjs(data.notificationSent).format("MMM D, YYYY")})
            </span>
          );
        } else {
          return <span style={{ color: "#D93131" }}>{t("No")}</span>;
        }
      },
    },
    {
      title: t("Action"),
      render: (data: IGymNotificationGym) => {
        return (
          <div className={"action-wrapper"}>
            <img src={dots} alt={"action"} />
            <ul className={"dropdown"}>
              <li
                className={"dropdown-item"}
                onClick={() => setSelectedGymIdForView(data.gymListingId)}
              >
                <img src={viewIcon} alt={"View"} />
                {t("View")}
              </li>
              <li className={"dropdown-item"} onClick={() => remove(data.gymListingId)}>
                <img style={{ width: "20px" }} src={basketIcon} alt={"delete"} />
                {t("Remove")}
              </li>
            </ul>
          </div>
        );
      },
    },
  ];

  return (
    <div className={"page-content gyms-page"}>
      <MainTitle text={t("GymNotifications")} />

      <div className={"top-row"}>
        <Form
          className={"form"}
          ref={formRef}
          name="gym-listing-filter"
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
        >
          <FormItemInput
            name={"searchValue"}
            label={t("Search")}
            rules={{ required: false }}
            placeholder={t("SearchByClassName")}
            style={{
              width: "30%",
            }}
          />
          <FormItemSelect
            name={"status"}
            label={t("Status")}
            rules={{ required: false }}
            items={selectItems}
            style={{
              width: "30%",
            }}
          />
          <div className={"btn-wrapper"}>
            <CustomButton
              text={""}
              type={"submit"}
              typeStyle={"fill"}
              icon={searchIconWhite}
              altIcon={"search"}
            />
          </div>
        </Form>
      </div>

      <Table
        className={"table"}
        columns={columns}
        rowKey={(record) => record.gymListingId}
        dataSource={data}
        pagination={{ ...tableParams, total: data.length }}
        loading={isLoading}
        onChange={handleTableChange}
      />

      {selectedGymIdForView && (
        <GymNotificationsUserPopup
          gymListingId={selectedGymIdForView}
          getData={refetch}
          closePopup={() => setSelectedGymIdForView(null)}
        />
      )}
    </div>
  );
};

export default GymNotifications;
